/*

FILE: utils.js
DESCRIPTION: Re-usable functions and utilities
AUTHOR(S): R.J. LaCount / Nick Katarow

*/

/**
 * @function
 * @description Helper for your function to not fire so frequently
 */
const debounce = (func, time) => {
    let timer;
    return (event) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(func, time || 300, event);
    };
};

/**
 * @type {Object}
 * @description Maps to breakpoint variables in variables.scss, use to see if a minimum breakpoint has been met by calling breakpoints.lg.matches
 */
const breakpoints = {
    sm: window.matchMedia('(min-width: 40rem)'),
    md: window.matchMedia('(min-width: 48rem)'),
    lg: window.matchMedia('(min-width: 64rem)'),
    xl: window.matchMedia('(min-width: 80rem)'),
    xxl: window.matchMedia('(min-width: 96rem)'),
};

/**
 * @function
 * @description Returns the current breakpoint
 */
// TOOD: THe following did not work. Need to explore further.
// const getBreakpoint = () => Object.keys(breakpoints).find((breakpoint) => breakpoints[breakpoint].matches);
/* eslint-disable no-restricted-syntax */
const getBreakpoint = () => {
    let currentBreakpoint;

    for (const breakpoint in breakpoints) {
        if (breakpoints[breakpoint].matches) {
            currentBreakpoint = breakpoint;
        } else {
            break;
        }
    }

    return currentBreakpoint;
};
/* eslint-enable no-restricted-syntax */

/**
 * @function
 * @description Check for named cookie
 */
const getCookie = (name) => {
    const dc = document.cookie;
    const prefix = `${name}=`;
    let begin = dc.indexOf(`; ${prefix}`);
    let end;
    if (begin === -1) {
        begin = dc.indexOf(prefix);
        if (begin !== 0) return null;
    } else {
        begin += 2;
        end = document.cookie.indexOf(';', begin);
        if (end === -1) {
            end = dc.length;
        }
    }
    // because unescape has been deprecated, replaced with decodeURI
    // return unescape(dc.substring(begin + prefix.length, end));
    return decodeURI(dc.substring(begin + prefix.length, end));
};

/**
 * @function
 * @description Set or update video files based on viewport & supported formats
 * @param {array} videos - video elements
 */
const supportVideoType = (type) => {
    const video = document.createElement('video');
    return video.canPlayType(type);
};

/**
 * @function
 * @description Set or update video files based on viewport & supported formats
 * @param {array} videos - video elements
 * @param {array} breakpoint - breakpoint element. Not sure what type it is.
 */
const updateVideoSource = (videos, breakpoint) => {
    const isLargeDisplay = ['lg', 'xl', 'xxl'].includes(breakpoint);
    let format;

    // Safari should be the only browser that need to use the quicktime format, but since it will also reply 'maybe' to webm, we need to test for quicktime first
    if (supportVideoType('application/vnd.apple.mpegURL') === 'probably' || supportVideoType('application/vnd.apple.mpegURL') === 'maybe') {
        format = 'video/quicktime';
    } else {
        format = 'video/webm';
    }

    videos.forEach((video) => {
        // Gather all sources that have the format we set above
        let supportedFormatSources = video.querySelectorAll(`[type="${format}"]`);
        supportedFormatSources = Array.from(supportedFormatSources);

        // From those sources, assign the large and small version to their own variables
        const largeSrc = supportedFormatSources.filter((source) => source.dataset.size === 'large')[0];
        const smallSrc = supportedFormatSources.filter((source) => source.dataset.size === 'small')[0];

        // If no src exists, initial load
        if (video.src === '') {
            if (isLargeDisplay) {
                video.src = largeSrc.dataset.src;
                video.dataset.size = largeSrc.dataset.size;
            } else {
                video.src = smallSrc.dataset.src;
                video.dataset.size = smallSrc.dataset.size;
            }
            // If a src exists, resize
        } else {
            if (isLargeDisplay && video.dataset.size !== 'large') {
                video.src = largeSrc.dataset.src;
                video.dataset.size = largeSrc.dataset.size;
            } else if (!isLargeDisplay && video.dataset.size !== 'small') {
                video.src = smallSrc.dataset.src;
                video.dataset.size = smallSrc.dataset.size;
            }

            // Look for videos that have already played and play them again.
            if (video.classList.contains('has-played')) {
                video.play();
            }
        }

        // Set height/width on video element to avoid layout jumping
        if (isLargeDisplay) {
            video.addEventListener('loadedmetadata', (e) => {
                const player = e.target;
                player.width = player.clientWidth;
                player.height = player.clientHeight;
            });
        }
    });
};

/**
 * @function
 * @description Duplicate and position a blurred version of the classed image behind and offset. Currently only used in blockquote-full module
 * @param {array} images - NodeList of elements with gaussian-blur class
 * @todo (optional) - extend for use with images not using picture element.
 */
const gaussianBlurImage = (images) => {
    images.forEach((image) => {
        const parent = image.parentNode;
        const duplicate = image.cloneNode(true);
        duplicate.className = 'blurred';
        parent.appendChild(duplicate);
    });
};

export {
    getBreakpoint, breakpoints, debounce, getCookie, updateVideoSource, gaussianBlurImage,
};
